import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import { Plus, Search, Eye, Edit, Trash2, ChevronLeft, ChevronRight, Copy, FileDown, Columns, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth.store'
import { employeesService } from '@/services/employeesService'
import { inboxTasksService } from '@/services/inboxTasksService'
import { Employee, EmployeeListResponse, DepartmentLookup } from '@/api/types'
import type { ApprovalStatusItem } from '@/api/types'

type EmployeeColumnKey = 'photo' | 'code' | 'fullName' | 'email' | 'mobile' | 'department' | 'isActive' | 'status'

const EMPLOYEE_LIST_COLUMNS: { key: EmployeeColumnKey; labelKey: string }[] = [
  { key: 'photo', labelKey: 'employees.photo' },
  { key: 'code', labelKey: 'employees.employeeCode' },
  { key: 'fullName', labelKey: 'employees.fullName' },
  { key: 'email', labelKey: 'employees.email' },
  { key: 'mobile', labelKey: 'employees.mobile' },
  { key: 'department', labelKey: 'employees.department' },
  { key: 'isActive', labelKey: 'employees.isActive' },
  { key: 'status', labelKey: 'employees.status' },
]

const EMPLOYEE_REPORT_HEADERS = [
  'Emp Code',
  'Full Name',
  'Reference',
  'Department',
  'Mobile',
  'Email',
  'Iqama No',
  'Passport No',
  'Joining Date',
  'Project',
  'Basic Salary',
  'Housing Allowance',
  'Transport Allowance',
  'Meal Allowance',
  'Other Allowance',
  'Gosi Deductions',
  'Other Deductions',
  'Bank Name',
  'Account Title',
  'IBAN',
  'Swift Code',
] as const

function csvCell(value: unknown): string {
  if (value === undefined || value === null || value === '') return ''
  return `"${String(value).replace(/"/g, '""')}"`
}

/** Keep codes/ids exactly as stored so Excel does not turn them into 9.66E+11. */
function csvExact(value: unknown): string {
  const raw = value === undefined || value === null ? '' : String(value).trim()
  if (!raw) return ''
  return `"=""${raw.replace(/"/g, '""')}"""`
}

/**
 * Employees List Page
 * Displays a table of employees with CRUD operations
 */
export const EmployeesList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const rtl = isRTL(i18n.language)
  const navigate = useNavigate()
  const { user } = useAuthStore()
  
  const [employees, setEmployees] = useState<Employee[]>([])
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [statuses, setStatuses] = useState<ApprovalStatusItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchQuery, setSearchQuery] = useState('')
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [exportingCsv, setExportingCsv] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState<Record<EmployeeColumnKey, boolean>>({
    photo: true,
    code: true,
    fullName: true,
    email: true,
    mobile: true,
    department: true,
    isActive: true,
    status: true,
  })

  const branchId = user?.selectedBranch?.branchId

  // Fetch employees
  useEffect(() => {
    if (!branchId) {
      toast.error('Please select a branch first')
      return
    }

    const fetchEmployees = async () => {
      try {
        setLoading(true)
        const response: EmployeeListResponse = await employeesService.getAllEmployees(
          branchId,
          pageNumber,
          pageSize
        )
        setEmployees(response.data || [])
        setTotalCount(response.totalRecords || 0)
        // Calculate total pages
        const totalPages = response.totalRecords ? Math.ceil(response.totalRecords / pageSize) : 0
        setTotalPages(totalPages)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setEmployees([])
        setTotalCount(0)
        setTotalPages(0)
        if (!isNoData) {
          toast.error(error.response?.data?.message || 'Failed to fetch employees')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchEmployees()
  }, [branchId, pageNumber, pageSize])

  // Fetch department lookups and status lookups (same as Task Inbox) for names
  useEffect(() => {
    const load = async () => {
      try {
        const [deptData, statusData] = await Promise.all([
          employeesService.getDepartmentLookups(),
          inboxTasksService.getApprovalStatuses(),
        ])
        setDepartments(deptData || [])
        setStatuses(statusData || [])
      } catch {
        setDepartments([])
        setStatuses([])
      }
    }
    load()
  }, [])

  // Client-side search filtering
  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) {
      return employees
    }

    const query = searchQuery.toLowerCase()
    return employees.filter((emp) => {
      const fullNameEn = `${emp.empFirstNameEn || ''} ${emp.empLastNameEn || ''}`.toLowerCase()
      const fullNameAr = `${emp.empFirstNameAr || ''} ${emp.empLastNameAr || ''}`.toLowerCase()
      const empCode = (emp.empCode || '').toLowerCase()
      const email = (emp.empEmail || '').toLowerCase()
      const mobile = (emp.empMobileNo || '').toLowerCase()

      return (
        fullNameEn.includes(query) ||
        fullNameAr.includes(query) ||
        empCode.includes(query) ||
        email.includes(query) ||
        mobile.includes(query)
      )
    })
  }, [employees, searchQuery])

  const handleAddEmployee = () => {
    navigate('/employees/new')
  }

  const handleViewEmployee = (id: number) => {
    navigate(`/employees/${id}/view`)
  }

  const handleEditEmployee = (id: number) => {
    navigate(`/employees/${id}/edit`)
  }

  const handleDeleteEmployee = async (employee: Employee) => {
    if (employeeIsApproved(employee)) return
    const name = getEmployeeFullName(employee)
    const confirmed = await showDeleteConfirm(name)
    if (!confirmed) return
    const id = employee.id!
    try {
      setDeletingId(id)
      await employeesService.deleteEmployee(id)
      await showSuccess('Employee deleted', 'Employee deleted successfully.', 3000)
      setEmployees((prev) => prev.filter((e) => e.id !== id))
      setTotalCount((prev) => Math.max(0, prev - 1))
    } catch (error: any) {
      await showError('Delete failed', error?.response?.data?.message ?? error?.message ?? 'Failed to delete employee.')
    } finally {
      setDeletingId(null)
    }
  }

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setPageNumber(1) // Reset to first page when page size changes
  }

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1)
    }
  }

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1)
    }
  }

  const getEmployeeFullName = (emp: Employee) => {
    const firstName = emp.empFirstNameEn || emp.empFirstNameAr || ''
    const lastName = emp.empLastNameEn || emp.empLastNameAr || ''
    return `${firstName} ${lastName}`.trim() || 'N/A'
  }

  const departmentMap = useMemo(() => {
    const map: Record<number, string> = {}
    departments.forEach((d) => {
      if (d.id) {
        map[d.id] = d.lookupNameEn || d.lookupNameAr || `Dept ${d.id}`
      }
    })
    return map
  }, [departments])

  const statusNameById = useMemo(() => {
    const map: Record<number, string> = {}
    statuses.forEach((s) => {
      if (s.id) map[s.id] = s.lookupNameEn || s.lookupNameAr || ''
    })
    return map
  }, [statuses])

  const employeeIsApproved = useCallback(
    (employee: Employee): boolean => {
      if (employee.status == null || employee.status <= 0) return false
      const item = statuses.find((s) => s.id === employee.status)
      if (item) {
        if ((item.lookupNameEn || '').trim().toLowerCase() === 'approved') return true
        if ((item.lookupNameAr || '').trim() === 'معتمد') return true
      }
      const lbl = (statusNameById[employee.status] || '').trim()
      return lbl.toLowerCase() === 'approved' || lbl === 'معتمد'
    },
    [statuses, statusNameById],
  )

  const getEmployeeStatusLabel = (emp: Employee): string => {
    if (emp.status != null && emp.status > 0 && statusNameById[emp.status]) return statusNameById[emp.status]
    return '—'
  }

  const toggleEmployeeColumn = (key: EmployeeColumnKey) => {
    setVisibleColumns((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      if (!Object.values(next).some(Boolean)) return prev
      return next
    })
  }

  const buildEmployeeExportCells = (emp: Employee): string[] => {
    const cells: string[] = []
    for (const { key } of EMPLOYEE_LIST_COLUMNS) {
      if (!visibleColumns[key]) continue
      switch (key) {
        case 'photo':
          cells.push(String(emp.imageUrl || emp.profileImageUrl || ''))
          break
        case 'code':
          cells.push(String(emp.empCode || ''))
          break
        case 'fullName':
          cells.push(getEmployeeFullName(emp))
          break
        case 'email':
          cells.push(String(emp.empEmail || ''))
          break
        case 'mobile':
          cells.push(String(emp.empMobileNo || ''))
          break
        case 'department':
          cells.push(String(departmentMap[emp.empDepartmetId ?? 0] || emp.departmentName || ''))
          break
        case 'isActive':
          cells.push(emp.isActive === 1 ? 'Active' : 'Inactive')
          break
        case 'status':
          cells.push(getEmployeeStatusLabel(emp))
          break
      }
    }
    return cells
  }

  const handleEmployeeCopy = () => {
    if (filteredEmployees.length === 0) {
      toast.error(t('employees.nothingToCopy'))
      return
    }
    const headers = EMPLOYEE_LIST_COLUMNS.filter((c) => visibleColumns[c.key]).map((c) => t(c.labelKey))
    const lines = filteredEmployees.map((emp) => buildEmployeeExportCells(emp).join('\t'))
    void navigator.clipboard.writeText([headers.join('\t'), ...lines].join('\n'))
    toast.success(t('employees.copiedToClipboard'))
  }

  const handleEmployeeExportCSV = async () => {
    if (!branchId) {
      toast.error('Please select a branch first')
      return
    }
    try {
      setExportingCsv(true)
      const rows = await employeesService.getEmployeeReport(branchId)
      const query = searchQuery.trim().toLowerCase()
      const filtered = !query
        ? rows
        : rows.filter((row) =>
            [
              row.empCode,
              row.fullName,
              row.reference,
              row.department,
              row.mobile,
              row.email,
              row.iqamaNo,
            ]
              .join(' ')
              .toLowerCase()
              .includes(query),
          )
      if (filtered.length === 0) {
        toast.error(t('employees.nothingToExport'))
        return
      }
      const headerLine = EMPLOYEE_REPORT_HEADERS.map((h) => csvCell(h)).join(',')
      const body = filtered
        .map((row) =>
          [
            csvExact(row.empCode),
            csvCell(row.fullName),
            csvExact(row.reference),
            csvCell(row.department),
            csvExact(row.mobile),
            csvCell(row.email),
            csvExact(row.iqamaNo),
            csvExact(row.passportNo),
            csvExact(row.joiningDate),
            csvCell(row.project),
            csvCell(row.basicSalary),
            csvCell(row.housingAllowance),
            csvCell(row.transportAllowance),
            csvCell(row.mealAllowance),
            csvCell(row.otherAllowance),
            csvCell(row.gosiDeductions),
            csvCell(row.otherDeductions),
            csvCell(row.bankName),
            csvCell(row.accountTitle),
            csvExact(row.iban),
            csvExact(row.swiftCode),
          ].join(','),
        )
        .join('\n')
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      const blob = new Blob([`\uFEFF${headerLine}\n${body}`], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Employee_Report_${stamp}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      toast.success(t('employees.exportedCsv'))
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('employees.nothingToExport'))
    } finally {
      setExportingCsv(false)
    }
  }

  const getEmployeeInitials = (emp: Employee) => {
    const firstName = emp.empFirstNameEn || emp.empFirstNameAr || ''
    const lastName = emp.empLastNameEn || emp.empLastNameAr || ''
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'NA'
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('navigation.employees')}
          description="Manage your organization's employees"
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title="Branch Not Selected"
              description="Please select a branch to view employees."
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.employees')}
        description="Manage your organization's employees"
        action={{
          label: 'Add New Employee',
          onClick: handleAddEmployee,
          icon: Plus,
        }}
      />

      {/* Search + export / column visibility */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search employees by name, code, email, or mobile..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" type="button">
                    <Columns className="mr-2 h-4 w-4" />
                    {t('employees.columnVisibility')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{t('employees.columnsToggle')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {EMPLOYEE_LIST_COLUMNS.map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={visibleColumns[col.key]}
                      onCheckedChange={() => toggleEmployeeColumn(col.key)}
                    >
                      {t(col.labelKey)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => void handleEmployeeExportCSV()}
                disabled={exportingCsv}
              >
                {exportingCsv ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="mr-2 h-4 w-4" />
                )}
                CSV
              </Button>
              <Button variant="outline" size="sm" type="button" onClick={handleEmployeeCopy}>
                <Copy className="mr-2 h-4 w-4" />
                {t('employees.listCopy')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading employees...</p>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={searchQuery ? "No employees found" : "No employees found"}
                description={
                  searchQuery
                    ? "Try adjusting your search query"
                    : "Get started by adding your first employee to the system."
                }
                action={
                  !searchQuery
                    ? {
                        label: 'Add New Employee',
                        onClick: handleAddEmployee,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    {visibleColumns.photo && (
                      <TableHead
                        className={`w-[60px] h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right' : 'text-left'}`}
                      >
                        {t('employees.photo') || 'Photo'}
                      </TableHead>
                    )}
                    {visibleColumns.code && (
                      <TableHead
                        className={`h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right' : 'text-left'}`}
                      >
                        {t('employees.employeeCode') || 'Employee Code'}
                      </TableHead>
                    )}
                    {visibleColumns.fullName && (
                      <TableHead
                        className={`h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right' : 'text-left'}`}
                      >
                        {t('employees.fullName') || 'Full Name'}
                      </TableHead>
                    )}
                    {visibleColumns.email && (
                      <TableHead
                        className={`h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right' : 'text-left'}`}
                      >
                        {t('employees.email') || 'Email'}
                      </TableHead>
                    )}
                    {visibleColumns.mobile && (
                      <TableHead
                        className={`h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right' : 'text-left'}`}
                      >
                        {t('employees.mobile') || 'Mobile'}
                      </TableHead>
                    )}
                    {visibleColumns.department && (
                      <TableHead
                        className={`h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right' : 'text-left'}`}
                      >
                        {t('employees.department') || 'Department'}
                      </TableHead>
                    )}
                    {visibleColumns.isActive && (
                      <TableHead
                        className={`h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right' : 'text-left'}`}
                      >
                        {t('employees.isActive') || 'Is Active'}
                      </TableHead>
                    )}
                    {visibleColumns.status && (
                      <TableHead
                        className={`h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right' : 'text-left'}`}
                      >
                        {t('employees.status') || 'Status'}
                      </TableHead>
                    )}
                    <TableHead className="h-12 px-3 text-right bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('employees.actions') || 'Actions'}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map((employee) => (
                    <TableRow 
                      key={employee.id} 
                      className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors cursor-pointer"
                    >
                      {visibleColumns.photo && (
                        <TableCell className="px-3 py-2">
                          <Avatar className="h-9 w-9">
                            <AvatarImage
                              src={employee.imageUrl || employee.profileImageUrl}
                              alt={getEmployeeFullName(employee)}
                            />
                            <AvatarFallback className="text-xs">
                              {getEmployeeInitials(employee)}
                            </AvatarFallback>
                          </Avatar>
                        </TableCell>
                      )}
                      {visibleColumns.code && (
                        <TableCell className={`px-3 py-2 font-medium text-sm ${rtl ? 'text-right' : 'text-left'}`}>
                          {employee.empCode || 'N/A'}
                        </TableCell>
                      )}
                      {visibleColumns.fullName && (
                        <TableCell className={`px-3 py-2 text-sm ${rtl ? 'text-right' : 'text-left'}`}>
                          {getEmployeeFullName(employee)}
                        </TableCell>
                      )}
                      {visibleColumns.email && (
                        <TableCell className={`px-3 py-2 text-sm ${rtl ? 'text-right' : 'text-left'}`}>
                          {employee.empEmail || 'N/A'}
                        </TableCell>
                      )}
                      {visibleColumns.mobile && (
                        <TableCell className={`px-3 py-2 text-sm ${rtl ? 'text-right' : 'text-left'}`}>
                          {employee.empMobileNo || 'N/A'}
                        </TableCell>
                      )}
                      {visibleColumns.department && (
                        <TableCell className={`px-3 py-2 text-sm ${rtl ? 'text-right' : 'text-left'}`}>
                          {departmentMap[employee.empDepartmetId ?? 0] || employee.departmentName || 'N/A'}
                        </TableCell>
                      )}
                      {visibleColumns.isActive && (
                        <TableCell className="px-3 py-2">
                          <Badge
                            variant={employee.isActive === 1 ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {employee.isActive === 1 ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                      )}
                      {visibleColumns.status && (
                        <TableCell className={`px-3 py-2 text-sm ${rtl ? 'text-right' : 'text-left'}`}>
                          {employee.status != null && employee.status > 0 && statusNameById[employee.status] ? (
                            (() => {
                              const statusText = getEmployeeStatusLabel(employee)
                              const isApproved = statusText.toLowerCase() === 'approved'
                              return (
                                <Badge variant={isApproved ? 'default' : 'outline'} className="text-xs">
                                  {statusText}
                                </Badge>
                              )
                            })()
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      )}
                      <TableCell className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewEmployee(employee.id!)}
                            className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditEmployee(employee.id!)}
                            className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteEmployee(employee)
                            }}
                            disabled={deletingId === employee.id || employeeIsApproved(employee)}
                            className="h-7 w-7 p-0 hover:bg-destructive/10 hover:text-destructive dark:hover:bg-destructive/20 disabled:opacity-40 disabled:pointer-events-none"
                            title={
                              employeeIsApproved(employee)
                                ? t('employees.deleteDisabledApproved')
                                : t('employees.delete', { defaultValue: 'Delete employee' })
                            }
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Page size + pagination */}
              <div className="border-t px-4 py-4 sm:px-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-muted-foreground text-center sm:text-left">
                    {searchQuery ? (
                      <span>
                        Showing {filteredEmployees.length} of {totalCount} employees
                        {searchQuery && ` (filtered from ${totalCount} total)`}
                      </span>
                    ) : (
                      <span>
                        Showing {(pageNumber - 1) * pageSize + 1} to{' '}
                        {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} employees
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end">
                    <div className="flex items-center justify-center gap-2 sm:justify-end">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">{t('common.pageSize')}</span>
                      <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                        <SelectTrigger className="w-[100px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {!searchQuery && (
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handlePreviousPage}
                          disabled={pageNumber === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          {t('common.previous')}
                        </Button>
                        <div className="text-sm whitespace-nowrap">
                          {t('common.page')} {pageNumber} {t('common.of')} {totalPages || 1}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleNextPage}
                          disabled={pageNumber >= totalPages}
                        >
                          {t('common.next')}
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
